/**
 * Scroll Parallax — scroll-coupled "breathing" motion for hero media.
 *
 * Markup contract:
 *   <element data-parallax="subtle">      — translates -20px to +20px as the
 *                                            element passes through the viewport
 *
 * As the element's centre moves from below the viewport (progress = -1) to
 * above it (progress = +1), we write `--parallax-progress` on the element.
 * CSS turns that into a translateY in `src/css/tailwind.css`.
 *
 * The progress is reset to 0 when the element leaves the viewport, so a
 * far-offscreen element never shows a stale offset.
 *
 * Honours prefers-reduced-motion (bails at module load); IntersectionObserver
 * gates which elements receive scroll updates, so this scales to many targets.
 */
(function () {
  'use strict';

  if (typeof window === 'undefined') return;

  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  if (!('IntersectionObserver' in window) || typeof window.requestAnimationFrame !== 'function') {
    return;
  }

  var active = new Set();
  var ticking = false;
  var viewportHeight = window.innerHeight;

  function update() {
    ticking = false;
    var halfViewport = viewportHeight / 2;
    active.forEach(function (el) {
      var rect = el.getBoundingClientRect();
      var elementCenter = rect.top + rect.height / 2;
      var distance = halfViewport - elementCenter;
      var range = halfViewport + rect.height / 2;
      var progress = distance / range;
      if (progress > 1) progress = 1;
      if (progress < -1) progress = -1;
      el.style.setProperty('--parallax-progress', progress.toFixed(3));
    });
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  }

  function onResize() {
    viewportHeight = window.innerHeight;
    onScroll();
  }

  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        active.add(entry.target);
      } else {
        active.delete(entry.target);
        entry.target.style.setProperty('--parallax-progress', '0');
      }
    });
    if (active.size > 0) onScroll();
  }, { rootMargin: '20% 0px 20% 0px' });

  function init(root) {
    var scope = root || document;
    var targets = scope.querySelectorAll('[data-parallax]');
    targets.forEach(function (el) { observer.observe(el); });
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize, { passive: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) { init(event.target); });

  window.MemoketScrollParallax = { observe: init };
})();
